import { supabase } from "@/integrations/supabase/client";

export interface RoomChatMessage {
  id: string;
  room_id: string;
  user_id: string;
  role: string;
  message: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  sender_name: string | null;
  sender_email: string | null;
}

export async function loadRoomChatMessages(roomId: string): Promise<RoomChatMessage[]> {
  // Fetch messages without profile join (no FK exists)
  const { data, error } = await supabase
    .from("room_chat_messages")
    .select("*")
    .eq("room_id", roomId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error loading room chat messages:", error);
    return [];
  }

  if (!data || data.length === 0) return [];

  // Get unique user_ids and batch-fetch profiles
  const userIds = [...new Set(data.map((row) => row.user_id))];
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, name, email")
    .in("id", userIds);

  const profileMap = new Map<string, { name: string | null; email: string }>();
  if (profiles) {
    for (const p of profiles) {
      profileMap.set(p.id, { name: p.name, email: p.email });
    }
  }

  return data.map((row) => {
    const profile = profileMap.get(row.user_id);
    return {
      id: row.id,
      room_id: row.room_id,
      user_id: row.user_id,
      role: row.role,
      message: row.message,
      metadata: row.metadata as Record<string, unknown> | null,
      created_at: row.created_at,
      sender_name: profile?.name ?? null,
      sender_email: profile?.email ?? null,
    };
  });
}

export async function saveRoomChatMessage({
  roomId,
  role,
  message,
  metadata = {},
}: {
  roomId: string;
  role: "user" | "assistant";
  message: string;
  metadata?: Record<string, unknown>;
}): Promise<RoomChatMessage | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await (supabase as any)
    .from("room_chat_messages")
    .insert({
      room_id: roomId,
      user_id: user.id,
      role,
      message,
      metadata,
    })
    .select("*")
    .single();

  if (error) {
    console.error("Error saving room chat message:", error);
    return null;
  }

  return data as RoomChatMessage;
}

export async function clearRoomChatMessages(roomId: string): Promise<boolean> {
  const { error } = await (supabase as any)
    .from("room_chat_messages")
    .delete()
    .eq("room_id", roomId);

  if (error) {
    console.error("Error clearing room chat messages:", error);
    return false;
  }

  return true;
}
